import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { BottomSheet } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Ban, ShieldCheck, Settings2, Loader2, Wallet, Plus, Minus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getWebApp, haptic, confirmAction } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import type { AdminPartner, PartnerMarkupOverride } from '@/api/types';

export const Route = createFileRoute('/partners')({ component: Partners });

// Живой курс USD→RUB из fxRates() (react-query дедупает запрос по ключу).
// Fallback 100 — только на миг загрузки; на проде курс из CBR всегда есть.
function useUsdToRub(): number {
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: () => api.fxRates() });
  return useMemo(() => fxRates?.find((r) => r.code === 'RUB')?.perRub ?? 100, [fxRates]);
}

/** USD-cents → «N ₽» по живому курсу (cents × rate / 100). */
function rub(usdMinor: number, rate: number): string {
  return `${Math.round((usdMinor * rate) / 100).toLocaleString('ru')} ₽`;
}

function Partners() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const usdToRub = useUsdToRub();
  const { data: partners, isPending } = useQuery({
    queryKey: ['partners'],
    queryFn: () => qc.getQueryData<AdminPartner[]>(['partners']) ?? api.partners(),
    placeholderData: keepPreviousData,
  });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminPartner | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const filtered = (partners || []).filter((p) => {
    const q = search.toLowerCase();
    return (
      !q ||
      p.firstName.toLowerCase().includes(q) ||
      (p.username || '').toLowerCase().includes(q) ||
      String(p.tgId).includes(q)
    );
  });

  return (
    <motion.div initial={false} className="p-4 space-y-4 pb-24">
      <h2 className="text-2xl font-bold tracking-tight px-2 pt-2">{t('Partners')}</h2>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search partners')}
          className="w-full bg-card border border-white/5 rounded-2xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="space-y-3">
        {isPending && !partners ? (
          <>
            <Skeleton className="h-[80px] rounded-2xl" />
            <Skeleton className="h-[80px] rounded-2xl" />
            <Skeleton className="h-[80px] rounded-2xl" />
            <Skeleton className="h-[80px] rounded-2xl" />
          </>
        ) : null}
        {filtered.map((p) => (
          <Card key={p.id} onClick={() => setSelected(p)} className="cursor-pointer hover:bg-white/[0.02] transition-colors">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-bold shrink-0">
                {p.firstName[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {p.firstName}
                  {p.username && <span className="text-muted-foreground"> @{p.username}</span>}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {p.activeSubs} subs · {p.botsCount} bots · {rub(p.totalEarnedUsdMinor, usdToRub)}
                </div>
                <div className="text-[11px] text-primary/90 truncate mt-0.5">
                  <Wallet className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                  Баланс: {rub(p.balanceUsdMinor, usdToRub)}
                </div>
              </div>
              <StatusBadge status={p.status} />
            </CardContent>
          </Card>
        ))}
      </div>

      <PartnerSheet
        partner={selected}
        onClose={() => setSelected(null)}
        onOverride={() => setOverrideOpen(true)}
        onAdjustBalance={() => setAdjustOpen(true)}
      />

      {selected && (
        <OverrideMarkupSheet open={overrideOpen} onClose={() => setOverrideOpen(false)} partner={selected} />
      )}

      {selected && (
        <AdjustBalanceSheet open={adjustOpen} onClose={() => setAdjustOpen(false)} partner={selected} />
      )}
    </motion.div>
  );
}

function StatusBadge({ status }: { status: AdminPartner['status'] }) {
  const cfg = {
    active: { cls: 'bg-green-500/15 text-green-500', label: 'ACTIVE' },
    banned: { cls: 'bg-red-500/15 text-red-500', label: 'BANNED' },
    pending_onboarding: { cls: 'bg-yellow-500/15 text-yellow-500', label: 'PENDING' },
  }[status];
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

function PartnerSheet({ partner, onClose, onOverride, onAdjustBalance }: { partner: AdminPartner | null; onClose: () => void; onOverride: () => void; onAdjustBalance: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const usdToRub = useUsdToRub();

  const { mutate: toggleBan, isPending: banPending } = useMutation({
    mutationFn: (p: AdminPartner) =>
      api.banPartner(p.id, p.status !== 'banned').then(() => p),
    onSuccess: (p) => {
      const next: AdminPartner['status'] = p.status === 'banned' ? 'active' : 'banned';
      qc.setQueryData<AdminPartner[]>(['partners'], (prev) =>
        (prev || []).map((x) => (x.id === p.id ? { ...x, status: next } : x))
      );
      haptic(next === 'banned' ? 'warning' : 'success');
      toast.success(next === 'banned' ? `${p.firstName} заблокирован` : `${p.firstName} разблокирован`);
      onClose();
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  if (!partner) return null;
  const isBanned = partner.status === 'banned';

  return (
    <BottomSheet open={!!partner} onClose={onClose} title={partner.firstName}>
      <div className="-mt-3">
        {partner.username && <p className="text-sm text-muted-foreground">@{partner.username}</p>}
        <p className="text-[11px] text-muted-foreground mt-1">tg id {partner.tgId}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Subs" value={String(partner.activeSubs)} />
        <Stat label="Bots" value={String(partner.botsCount)} />
        <Stat label="Earned" value={rub(partner.totalEarnedUsdMinor, usdToRub)} />
        <Stat label="Balance" value={rub(partner.balanceUsdMinor, usdToRub)} />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={onAdjustBalance}
          className="w-full p-4 flex items-center gap-3 rounded-2xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
        >
          <Wallet className="w-5 h-5 text-primary" />
          <span className="font-semibold">Изменить баланс</span>
        </button>
        <button
          type="button"
          onClick={onOverride}
          className="w-full p-4 flex items-center gap-3 rounded-2xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
        >
          <Settings2 className="w-5 h-5 text-primary" />
          <span className="font-semibold">{t('Override markup')}</span>
        </button>
        <button
          type="button"
          disabled={banPending}
          onClick={async () => {
            const msg = isBanned
              ? `Разблокировать ${partner.firstName}?`
              : `Заблокировать ${partner.firstName}? Партнёр перестанет видеть кабинет, его боты остановятся.`;
            if (!(await confirmAction(msg))) return;
            toggleBan(partner);
          }}
          className={`w-full p-4 flex items-center gap-3 rounded-2xl transition-colors disabled:opacity-50 ${
            isBanned ? 'bg-green-500/10 hover:bg-green-500/15 text-green-500' : 'bg-red-500/10 hover:bg-red-500/15 text-red-500'
          }`}
        >
          {isBanned ? <ShieldCheck className="w-5 h-5" /> : <Ban className="w-5 h-5" />}
          <span className="font-semibold">{isBanned ? t('Unban') : t('Ban')}</span>
        </button>
      </div>
    </BottomSheet>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/[0.02] rounded-xl p-3">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="font-bold text-sm truncate">{value}</div>
    </div>
  );
}

function OverrideMarkupSheet({ open, onClose, partner }: { open: boolean; onClose: () => void; partner: AdminPartner }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [cap, setCap] = useState<number | ''>('');
  const [floor, setFloor] = useState<number | ''>('');

  // Подгружаем текущие cap/floor при открытии — иначе sheet стартует пустым
  // и owner затирает значения которые не видит. Endpoint уже есть (GET markup-override).
  const { data: current } = useQuery({
    queryKey: ['partner-override', partner.id],
    queryFn: () => api.partnerOverrideGet(partner.id),
    enabled: open,
  });
  useEffect(() => {
    if (!open) return;
    setCap(current?.markup_cap_percent ?? '');
    setFloor(current?.markup_floor_percent ?? '');
  }, [open, current]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      api.partnerOverride(partner.id, {
        markup_cap_percent: typeof cap === 'number' ? cap : null,
        markup_floor_percent: typeof floor === 'number' ? floor : null,
      }),
    onSuccess: () => {
      qc.setQueryData<PartnerMarkupOverride[]>(['partner-overrides'], (prev) => {
        const next: PartnerMarkupOverride = {
          partnerId: partner.id,
          markupCapPercent: typeof cap === 'number' ? cap : null,
          markupFloorPercent: typeof floor === 'number' ? floor : null,
        };
        return [...(prev || []).filter((o) => o.partnerId !== partner.id), next];
      });
      qc.invalidateQueries({ queryKey: ['partner-override', partner.id] });
      haptic('success');
      toast.success('Override сохранён');
      onClose();
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  const reset = () => {
    setCap('');
    setFloor('');
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={`Override · ${partner.firstName}`}>
      <p className="text-sm text-muted-foreground">
        Ограничивает или задаёт минимум для наценки партнёра. Оставь пустым, чтобы не ограничивать.
      </p>

      <Field label={t('Markup cap')}>
        <input
          type="number"
          value={cap}
          onChange={(e) => setCap(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder="например 100"
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </Field>

      <Field label={t('Markup floor')}>
        <input
          type="number"
          value={floor}
          onChange={(e) => setFloor(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder="например 10"
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={reset} className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold">
          {t('Reset')}
        </button>
        <button
          type="button"
          onClick={() => save()}
          disabled={isPending}
          className="bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('Save')}
        </button>
      </div>
    </BottomSheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

function AdjustBalanceSheet({
  open,
  onClose,
  partner,
}: {
  open: boolean;
  onClose: () => void;
  partner: AdminPartner;
}) {
  const qc = useQueryClient();
  const usdToRub = useUsdToRub();
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amountUsd, setAmountUsd] = useState<string>('');
  const [reason, setReason] = useState('');

  const amountNum = parseFloat(amountUsd.replace(',', '.'));
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const reasonValid = reason.trim().length >= 3;
  const canSubmit = amountValid && reasonValid;

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const signed = direction === 'credit' ? amountNum : -amountNum;
      const amount_minor = Math.round(signed * 100);
      return api.adjustPartnerBalance(partner.id, {
        amount_minor,
        reason: reason.trim(),
      });
    },
    onSuccess: (resp) => {
      haptic('success');
      const sign = direction === 'credit' ? '+' : '-';
      toast.success(
        `Баланс ${partner.firstName}: ${sign}$${amountNum.toFixed(2)} → новый $${(resp.new_balance_minor / 100).toFixed(2)}`,
      );
      qc.invalidateQueries({ queryKey: ['partners'] });
      setAmountUsd('');
      setReason('');
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось скорректировать баланс');
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Изменить баланс">
      <p className="text-sm text-muted-foreground">
        Партнёр: <b>{partner.firstName}</b>
        {partner.username && ` (@${partner.username})`}
        <br />
        Текущий баланс: <b>{rub(partner.balanceUsdMinor, usdToRub)}</b>
      </p>

      <Field label="Действие">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setDirection('credit')}
            className={`py-3 rounded-xl text-sm font-bold transition-colors ${
              direction === 'credit'
                ? 'bg-green-500/15 text-green-500 border border-green-500/30'
                : 'bg-white/[0.04] text-muted-foreground'
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" aria-hidden />Начислить</span>
          </button>
          <button
            type="button"
            onClick={() => setDirection('debit')}
            className={`py-3 rounded-xl text-sm font-bold transition-colors ${
              direction === 'debit'
                ? 'bg-red-500/15 text-red-500 border border-red-500/30'
                : 'bg-white/[0.04] text-muted-foreground'
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5"><Minus className="w-4 h-4" aria-hidden />Списать</span>
          </button>
        </div>
      </Field>

      <Field label="Сумма в USD">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          value={amountUsd}
          onChange={(e) => setAmountUsd(e.target.value)}
          placeholder="например 10.50"
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-[11px] text-muted-foreground">
          Хранится в USD (как и весь wallet). UI партнёра конвертит в ₽ по live-курсу.
        </p>
      </Field>

      <Field label="Причина (обязательно)">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="например: компенсация за инцидент с AdaptGroup 24.05"
          rows={2}
          maxLength={200}
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
        <p className="text-[11px] text-muted-foreground">
          Запишется в audit_log + description ledger-entries. Не удаляется.
        </p>
      </Field>

      <button
        type="button"
        disabled={!canSubmit || isPending}
        onClick={() => mutate()}
        className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform ${
          direction === 'credit'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        {direction === 'credit'
          ? `Начислить $${amountValid ? amountNum.toFixed(2) : '...'}`
          : `Списать $${amountValid ? amountNum.toFixed(2) : '...'}`}
      </button>
    </BottomSheet>
  );
}
